<template>
  <div
    class="metrics-custom-report-view"
    :class="{ 'is-exporting-pdf': isExportingPdf }"
  >
    <metrics-class-header
      :title="$t('metrics.customReport')"
      hide-demo-toggle
      :students-count="
        overallInfoData && overallInfoData.counters
          ? overallInfoData.counters.students
          : null
      "
      :assignments-count="assignmentIds.length || null"
      :is-page-loading="isPageLoading"
      :teachers-label="teachersLabel"
      :teacher-hint-assignments="assignmentNames"
      @export-pdf="exportToPdf"
      @print="print"
    />

    <div class="metrics-custom-report-view__content">
      <metrics-grade-distribution
        :overall-avg="overallInfoData && overallInfoData.overallAvg"
        :grades="overallInfoData && overallInfoData.studentGroups"
      />

      <metrics-class-charts-row
        :performance-trend-data="performanceTrendData"
        :assignment-summary-data="assignmentSummaryData"
        :assignment-base-path="assignmentBasePath"
        :assignment-class-map="assignmentClassMap"
        :is-performance-trend-loading="isPerformanceTrendLoading"
        :selected-period="selectedPeriod"
        @update:selectedPeriod="onSelectedPeriodChange"
      />

      <metrics-student-trajectories
        :trajectories="studentTrajectoriesData"
        :is-loading="isStudentTrajectoriesLoading"
        :suggestions="
          insightsData && insightsData.growthProfiles
            ? insightsData.growthProfiles.growthProfilesSuggestions
            : null
        "
      />

      <metrics-writing-skills-trajectory :insights="insightsData" />
    </div>
    <metrics-pdf-overlay v-if="isExportingPdf" />
  </div>
</template>

<script>
import MetricsClassHeader from "./components/class/metrics_class_header.vue";
import MetricsClassChartsRow from "./components/class/metrics_class_charts_row.vue";
import MetricsGradeDistribution from "./components/shared/metrics_grade_distribution.vue";
import MetricsStudentTrajectories from "./components/class/metrics_student_trajectories.vue";
import MetricsWritingSkillsTrajectory from "./components/shared/metrics_writing_skills_trajectory.vue";
import {
  getMetricsOverallInfo,
  getMetricsPerformanceTrends,
  getMetricsClassAssignmentSummary,
  getMetricsStudentGrowth,
  getMetricsClassInsights,
} from "@/api/requests/metrics.js";
import { getFoldersByEntity } from "@/api/requests/folder.js";
import { METRICS_TIME_FILTERS } from "./demo_data/shared.js";
import metricsViewMixin from "@/mixins/metricsViewMixin.js";
import MetricsPdfOverlay from "./components/shared/metrics_pdf_overlay.vue";
import metricsClassViewMixin from "@/mixins/metricsClassViewMixin.js";

export default {
  name: "MetricsCustomReportView",

  components: {
    MetricsClassHeader,
    MetricsClassChartsRow,
    MetricsGradeDistribution,
    MetricsStudentTrajectories,
    MetricsWritingSkillsTrajectory,
    MetricsPdfOverlay,
  },

  mixins: [metricsViewMixin, metricsClassViewMixin],

  data() {
    return {
      isExportingPdf: false,
      overallInfoData: null,
      assignmentSummaryData: [],
      insightsData: null,
    };
  },

  computed: {
    classIds() {
      return this.$store.getters["customReport/classIds"];
    },

    assignmentIds() {
      return this.$store.getters["customReport/assignmentIds"];
    },

    assignmentClassMap() {
      const map = {};
      this.$store.state.customReport.selectedClasses.forEach(
        (selectedClass) => {
          selectedClass.assignments.forEach((assignment) => {
            map[assignment.id] = selectedClass.id;
          });
        }
      );
      return map;
    },

    assignmentNames() {
      if (!this.assignmentIds.length) {
        return this.assignmentSummaryData
          .map((assignment) => assignment.name)
          .filter(Boolean);
      }
      return this.assignmentSummaryData
        .filter((assignment) => this.assignmentIds.includes(assignment.id))
        .map((assignment) => assignment.name)
        .filter(Boolean);
    },

    teachersLabel() {
      const { orgId } = this.$route.params;
      if (orgId) {
        const seenIds = new Set();
        const names = [];
        for (const selectedClass of this.$store.state.customReport
          .selectedClasses) {
          const user =
            this.$store.getters["orgUsers/getUserById"](selectedClass.userId) ||
            this.$store.getters["orgUsers/getUserByClerkId"](
              selectedClass.userId
            );
          if (user && !seenIds.has(user.id)) {
            seenIds.add(user.id);
            const name = `${user.firstName || ""} ${
              user.lastName || ""
            }`.trim();
            if (name) {
              names.push(name);
            }
          }
        }
        return names.join(", ") || null;
      }
      const storeUser = this.$store.state.user;
      if (!storeUser) return null;
      return (
        `${storeUser.firstName || ""} ${storeUser.lastName || ""}`.trim() ||
        null
      );
    },

    pdfFileName() {
      return "custom report";
    },

    isPageLoading() {
      return (
        this.isPerformanceTrendLoading ||
        this.isStudentTrajectoriesLoading ||
        this.overallInfoData === null
      );
    },

    assignmentBasePath() {
      return this.metricsBasePath;
    },
  },

  async mounted() {
    if (!this.$store.state.customReport.selectedClasses.length) {
      this.$router.replace(this.metricsBasePath);
      return;
    }
    await this.validateSelections();
    if (!this.$store.state.customReport.selectedClasses.length) {
      return;
    }
    if (this.$route.params.orgId) {
      this.$store.dispatch("orgUsers/load", this.entityId);
    }
    this.fetchOverallInfo();
    this.fetchAssignmentSummary();
    this.fetchInsights();
  },

  methods: {
    async validateSelections() {
      try {
        const data = await getFoldersByEntity(this.entityId);
        const availableIds = new Set();
        const collectIds = (folders) => {
          folders.forEach((folder) => {
            availableIds.add(folder.id);
            if (folder.children?.length) {
              collectIds(folder.children);
            }
          });
        };
        Object.values(data || {}).forEach(collectIds);

        const validClasses = this.$store.state.customReport.selectedClasses
          .filter((selectedClass) => availableIds.has(selectedClass.id))
          .map((selectedClass) => ({
            ...selectedClass,
            assignments: selectedClass.assignments.filter((assignment) =>
              availableIds.has(assignment.id)
            ),
          }))
          .filter((selectedClass) => selectedClass.assignments.length > 0);

        if (!validClasses.length) {
          this.$store.commit("customReport/clear");
          this.$router.replace(this.metricsBasePath);
          return;
        }

        this.$store.commit("customReport/setSelections", validClasses);
      } catch {
        // ignore — proceed with stored data if validation fails
      }
    },

    async fetchOverallInfo() {
      if (!this.entityId || !this.classIds.length) return;
      try {
        const result = await getMetricsOverallInfo({
          entityId: this.entityId,
          classIds: this.classIds,
          assignmentIds: this.assignmentIds,
        });
        this.overallInfoData = result?.data || null;
      } catch {
        this.overallInfoData = null;
      }
      this.fetchStudentGrowth();
    },

    async fetchAssignmentSummary() {
      if (!this.entityId || !this.classIds.length) return;
      try {
        const result = await getMetricsClassAssignmentSummary({
          entityId: this.entityId,
          classIds: this.classIds,
          limit: 50,
        });
        if (result?.data?.data) {
          const data = result.data.data;
          this.assignmentSummaryData = this.assignmentIds.length
            ? data.filter((assignment) =>
                this.assignmentIds.includes(assignment.id)
              )
            : data;
        }
      } catch {
        this.assignmentSummaryData = [];
      }
    },

    async fetchStudentGrowth() {
      if (!this.entityId || !this.classIds.length) return;
      this.isStudentTrajectoriesLoading = true;
      try {
        const result = await getMetricsStudentGrowth({
          entityId: this.entityId,
          classIds: this.classIds,
          assignmentIds: this.assignmentIds,
        });
        this.studentTrajectoriesData = result?.data || {
          summary: {},
          data: [],
        };
      } catch {
        this.studentTrajectoriesData = { summary: {}, data: [] };
      } finally {
        this.isStudentTrajectoriesLoading = false;
      }
      this.fetchPerformanceTrend();
    },

    async fetchPerformanceTrend() {
      if (!this.entityId || !this.classIds.length) return;
      this.isPerformanceTrendLoading = true;
      try {
        const filter =
          METRICS_TIME_FILTERS.find((f) => f.value === "all_time") ||
          METRICS_TIME_FILTERS[0];
        const timeParams = filter.getParams();
        const result = await getMetricsPerformanceTrends({
          entityId: this.entityId,
          classIds: this.classIds,
          assignmentIds: this.assignmentIds,
          dateFrom: timeParams.dateFrom,
          dateTo: timeParams.dateTo,
          period: this.selectedPeriod,
        });
        this.performanceTrendData = result
          ? result.data
          : { labels: [], datasets: [] };
      } catch {
        this.performanceTrendData = { labels: [], datasets: [] };
      } finally {
        this.isPerformanceTrendLoading = false;
      }
    },

    async fetchInsights() {
      if (!this.entityId || !this.classIds.length) return;
      try {
        const result = await getMetricsClassInsights({
          entityId: this.entityId,
          classId: this.classIds[0],
        });
        this.insightsData = result?.data || null;
      } catch {
        this.insightsData = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./components/shared/metrics_card";

.metrics-custom-report-view {
  @include metrics-view;
  gap: 16px;

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 24px;
  }
}
</style>
